// AI Tool System - Registry for AI tool definitions and classes
// Stores tools by unique name with support for model-specific overrides
// Handles tool execution with error wrapping

import { logger } from "../../../core/logger.js";

export class AIToolSystem {
  constructor() {
    // Map: toolName -> { "default": { definition, tool }, "modelId1": { definition, tool }, ... }
    this.tools = new Map();
    this.toolUiStyleBundles = new Map();

    // Quadrad peer references (wired alongside ToolSystem, AIInferenceSystem, BubbleUiSystem)
    this.toolSystem = null; // Reference to ToolSystem
    this.aiInferenceSystem = null; // Reference to AIInferenceSystem
    this.bubbleUiSystem = null; // Reference to BubbleUiSystem

  }

  /**
   * Register a tool class
   * @param {Function|Object} ToolClass - Tool class constructor or instance
   * @param {Array<string>|null} [modelIds=null] - Model IDs this tool applies to (null = all models)
   * @param {Object|null} [toolSet=null] - Tool set instance for back-references
   */
  registerTool(ToolClass, modelIds = null, toolSet = null) {
    if (!ToolClass) {
      throw new Error("Tool class is required");
    }

    const tool =
      typeof ToolClass === "function" ? new ToolClass() : ToolClass;

    if (!tool.toolId || typeof tool.toolId !== "string") {
      throw new Error("Tool class must define toolId string");
    }

    if (!tool.definition || !tool.definition.function) {
      throw new Error("Tool definition must have a function property");
    }

    if (tool.definition.function.name !== tool.toolId) {
      throw new Error(
        `Tool definition function.name (${tool.definition.function.name}) must match toolId (${tool.toolId})`
      );
    }

    if (typeof tool.execute !== "function") {
      throw new Error("Tool class must implement execute()");
    }

    tool.aiToolSystem = this;
    tool.aiToolSet = toolSet || null;

    const toolName = tool.toolId;
    const definition = tool.definition;

    // Normalize modelIds: null or empty array means all models (default)
    const isDefault =
      !modelIds || (Array.isArray(modelIds) && modelIds.length === 0);

    // Get or create tool entry
    let toolEntry = this.tools.get(toolName);
    if (!toolEntry) {
      toolEntry = {};
      this.tools.set(toolName, toolEntry);
    }

    if (isDefault) {
      // Registering for all models - clear all model-specific entries
      this.tools.set(toolName, {
        default: {
          definition,
          tool,
        },
      });

      logger.log(
        {
          tags: "tools|ai|register",
          color1: "lime",
        },
        `Registered tool '${toolName}' for all models (default)`
      );
    } else {
      // Registering for specific models - keep default, add/override model-specific entries
      const modelIdArray = Array.isArray(modelIds) ? modelIds : [modelIds];

      for (const modelId of modelIdArray) {
        if (typeof modelId !== "string") {
          throw new Error(`Model ID must be a string, got: ${typeof modelId}`);
        }

        toolEntry[modelId] = {
          definition,
          tool,
        };

        logger.log(
          {
            tags: "tools|ai|register",
            color1: "lime",
          },
          `Registered tool '${toolName}' for model '${modelId}'`
        );
      }

      // Update the map entry
      this.tools.set(toolName, toolEntry);
    }
  }

  /**
   * Resolve a tool name to a registered tool key.
   * Some models return snake_case (e.g. get_project_overview) when our tools use kebab-case (get-project-overview).
   * This normalizes predictable naming variations so we can still execute the tool.
   * @param {string} toolName - Tool identifier as returned by the model
   * @returns {string|null} Resolved tool key if found, null otherwise
   */
  _resolveToolName(toolName) {
    if (this.tools.has(toolName)) {
      return toolName;
    }
    // snake_case -> kebab-case: some models normalize our kebab-case tool names to snake_case
    const kebabCase = toolName.replace(/_/g, "-");
    if (this.tools.has(kebabCase)) {
      return kebabCase;
    }
    return null;
  }

  /**
   * Public alias for `_resolveToolName` — use for UI (bubble style, labels) so tool keys match execution.
   * @param {string} toolName
   * @returns {string|null} Registered tool key, or null if unknown
   */
  resolveRegisteredToolName(toolName) {
    return this._resolveToolName(toolName);
  }

  /**
   * Set the bubble UI system reference
   * @param {BubbleUiSystem} bubbleUiSystem - The bubble UI system instance
   */
  setBubbleUiSystem(bubbleUiSystem) {
    this.bubbleUiSystem = bubbleUiSystem;
  }

  /**
   * Get a tool for a specific model
   * @param {string} toolName - Tool identifier
   * @param {string} [modelId] - Optional model ID (checks model-specific first, then default)
   * @returns {Object|null} Tool data { definition, tool } or null if not found
   */
  getTool(toolName, modelId = null) {
    const resolvedName = this._resolveToolName(toolName);
    if (!resolvedName) {
      return null;
    }
    const toolEntry = this.tools.get(resolvedName);
    if (!toolEntry) {
      return null;
    }

    // If modelId provided, check model-specific first
    if (modelId && toolEntry[modelId]) {
      return toolEntry[modelId];
    }

    // Fall back to default
    if (toolEntry.default) {
      return toolEntry.default;
    }

    return null;
  }

  /**
   * Check if a tool exists for a model
   * @param {string} toolName - Tool identifier
   * @param {string} [modelId] - Optional model ID
   * @returns {boolean} True if tool exists
   */
  hasTool(toolName, modelId = null) {
    return this.getTool(toolName, modelId) !== null;
  }

  /**
   * Get all tools available for a specific model
   * @param {string} [modelId] - Optional model ID
   * @returns {Array<Object>} Array of tool definitions (OpenRouter format)
   */
  getToolsForModel(modelId = null) {
    const toolDefinitions = [];

    for (const [, toolEntry] of this.tools.entries()) {
      let toolData = null;

      // Check model-specific first if modelId provided
      if (modelId && toolEntry[modelId]) {
        toolData = toolEntry[modelId];
      } else if (toolEntry.default) {
        // Fall back to default
        toolData = toolEntry.default;
      }

      if (toolData) {
        toolDefinitions.push(toolData.definition);
      }
    }

    return toolDefinitions;
  }

  /**
   * Execute a tool for a specific model
   * @param {string} toolName - Tool identifier
   * @param {string} [modelId] - Optional model ID
   * @param {...any} args - Arguments to pass to tool (must match definition parameters)
   * @returns {Promise<Object>} Result object (tool result on success, { success: false, error: "message" } on error)
   */
  async executeTool(toolName, modelId = null, ...args) {
    const toolData = this.getTool(toolName, modelId);

    if (!toolData) {
      const errorMsg = modelId
        ? `Tool '${toolName}' not found for model '${modelId}'`
        : `Tool '${toolName}' not found`;
      logger.error(
        {
          tags: "tools|ai|execute|error",
          color1: "red",
        },
        errorMsg
      );
      return {
        success: false,
        error: errorMsg,
      };
    }

    const { tool } = toolData;

    try {
      logger.log(
        {
          tags: "tools|ai|execute",
          color1: "cyan",
        },
        `Executing tool '${toolName}'${modelId ? ` for model '${modelId}'` : ""}`,
        { ...args }
      );

      // Call tool instance with options object (matches definition parameters)
      const result = await tool.execute(...args);

      logger.log(
        {
          tags: "tools|ai|execute|result",
          color1: "lime",
        },
        `Tool '${toolName}' executed successfully`,
        { result }
      );

      // Return result directly (should be a JSON object)
      return result;
    } catch (error) {
      const errorMessage = error.message || "Unknown error occurred";
      logger.error(
        {
          tags: "tools|ai|execute|error",
          color1: "red",
        },
        `Tool '${toolName}' execution failed`,
        { error: errorMessage, stack: error.stack }
      );

      // Return standardized error object
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Clear all registered tools
   */
  clear() {
    this.tools.clear();
    this.toolUiStyleBundles.clear();
    logger.log(
      {
        tags: "tools|ai|clear",
        color1: "lime",
      },
      "Cleared all AI tools"
    );
  }

  /**
   * Get all registered tool names
   * @returns {Array<string>} Array of tool names
   */
  getToolNames() {
    return Array.from(this.tools.keys());
  }

  /**
   * Register a toolset and set up its AI tool system reference
   * @param {Object} toolSet - Tool set instance with registerTools method
   */
  registerToolSet(toolSet) {
    if (!toolSet || typeof toolSet.registerTools !== "function") {
      throw new Error("Tool set must have a registerTools method");
    }

    // Set the AI tool system reference on the tool set
    toolSet.aiToolSystem = this;

    const styleBundles =
      typeof toolSet.getToolUiStyleBundles === "function"
        ? toolSet.getToolUiStyleBundles()
        : [];
    if (Array.isArray(styleBundles)) {
      styleBundles.forEach((bundle) => {
        const id = bundle?.id;
        const cssText = bundle?.cssText;
        if (
          typeof id === "string" &&
          id.trim().length > 0 &&
          typeof cssText === "string" &&
          cssText.trim().length > 0
        ) {
          this.toolUiStyleBundles.set(id, {
            id,
            cssText,
          });
        }
      });
    }

    // Register the tools
    toolSet.registerTools();
  }

  /**
   * Set the tool system reference
   * @param {ToolSystem} toolSystem - The tool system instance
   */
  setToolSystem(toolSystem) {
    this.toolSystem = toolSystem;
  }

  /**
   * Set the AI inference system reference
   * @param {AIInferenceSystem} aiInferenceSystem - The AI inference system instance
   */
  setAIInferenceSystem(aiInferenceSystem) {
    this.aiInferenceSystem = aiInferenceSystem;
  }

  getToolUiStyleBundles() {
    return Array.from(this.toolUiStyleBundles.values());
  }

  getToolInstance(toolName, modelId = null) {
    const toolData = this.getTool(toolName, modelId);
    return toolData?.tool || null;
  }

  hasCheckpointEntry(entryId, responseMessageId = null) {
    return (
      this.aiInferenceSystem?.hasCheckpointEntry?.(entryId, responseMessageId) ||
      false
    );
  }

  createCheckpointEntry(entryId, entry, responseMessageId = null) {
    return (
      this.aiInferenceSystem?.createCheckpointEntry?.(
        entryId,
        entry,
        responseMessageId
      ) || false
    );
  }

  createRedoCheckpointEntry(entryId, entry) {
    return (
      this.aiInferenceSystem?.createRedoCheckpointEntry?.(entryId, entry) ||
      false
    );
  }
}
